import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import {
  Loader2, Plus, Trash2, Save, Send, FlaskConical, Users, MessageSquare,
  MessagesSquare, Radio, RefreshCw, Hash, AlertTriangle, CheckCircle2,
} from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-ink/10 text-ink-soft",
  sending: "bg-sage/25 text-ink",
  sent: "bg-sage/30 text-ink",
};

export default function AdminMessaging() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [tab, setTab] = useState("sms");

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-messaging">
      <div className="mx-auto max-w-5xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div>
          <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink">
            <MessagesSquare className="h-6 w-6 text-gold-deep" /> Messaging
          </h1>
          <p className="mt-1 text-sm text-ink-soft">
            Reach your readers beyond the inbox — a text message to the phone numbers you've gathered, or a Telegram broadcast to those who opted in.
          </p>
        </div>

        <div className="mt-6 inline-flex rounded-full bg-ivory-50 p-1 ring-1 ring-ivory-400/60" data-testid="messaging-tabs">
          <button onClick={() => setTab("sms")} data-testid="messaging-tab-sms"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-display text-sm transition ${tab === "sms" ? "bg-ink text-ivory-50" : "text-ink-soft hover:text-ink"}`}>
            <MessageSquare className="h-4 w-4" /> Text (SMS)
          </button>
          <button onClick={() => setTab("telegram")} data-testid="messaging-tab-telegram"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-display text-sm transition ${tab === "telegram" ? "bg-ink text-ivory-50" : "text-ink-soft hover:text-ink"}`}>
            <Radio className="h-4 w-4" /> Telegram
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 py-7 sm:px-8">
        {tab === "sms" ? <SmsPanel auth={auth} /> : <TelegramPanel auth={auth} />}
      </div>
    </div>
  );
}

/* ----------------------------- SMS ----------------------------- */
const blankSms = () => ({ id: "new", name: "", body: "", segment: "all", tag: "", status: "draft", stats: {} });

function SmsPanel({ auth }) {
  const [status, setStatus] = useState({ ready: null, from_number: "" });
  const [rows, setRows] = useState([]);
  const [segments, setSegments] = useState([]);
  const [tags, setTags] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(blankSms());
  const [aud, setAud] = useState({ count: null, sample: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [testPhone, setTestPhone] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, c, seg] = await Promise.all([
        axios.get(`${API}/admin/sms/status`, auth),
        axios.get(`${API}/admin/sms/campaigns`, auth),
        axios.get(`${API}/admin/campaign-segments`, auth),
      ]);
      setStatus(st.data);
      setRows(c.data.campaigns || []);
      setSegments(seg.data.segments || []);
      setTags(seg.data.tags || []);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load SMS.");
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const refreshAud = useCallback(async (segment, tag) => {
    try {
      const { data } = await axios.get(`${API}/admin/sms/audience`, { ...auth, params: { segment: segment || "all", tag: tag || undefined } });
      setAud(data);
    } catch { setAud({ count: null, sample: [] }); }
  }, [auth]);

  useEffect(() => { if (sel) refreshAud(form.segment, form.tag); }, [sel, form.segment, form.tag, refreshAud]);

  const openNew = () => { setSel("new"); setForm(blankSms()); setMsg(""); setErr(""); };
  const openRow = (r) => { setSel(r.id); setForm({ ...blankSms(), ...r }); setMsg(""); setErr(""); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const payload = () => ({ name: form.name, body: form.body, segment: form.segment || "all", tag: form.tag || "" });
  const isSent = form.status === "sent" || form.status === "sending";

  const save = async () => {
    setBusy("save"); setErr(""); setMsg("");
    try {
      if (sel === "new") {
        const { data } = await axios.post(`${API}/admin/sms/campaigns`, payload(), auth);
        setRows((r) => [data, ...r]); setSel(data.id); setForm({ ...blankSms(), ...data }); setMsg("Draft saved.");
      } else {
        const { data } = await axios.put(`${API}/admin/sms/campaigns/${sel}`, payload(), auth);
        setRows((r) => r.map((x) => (x.id === sel ? data : x))); setForm((f) => ({ ...f, ...data })); setMsg("Saved.");
      }
    } catch (e) { setErr(e.response?.data?.detail || "Could not save."); } finally { setBusy(""); }
  };

  const sendTest = async () => {
    if (sel === "new") { setErr("Save first."); return; }
    if (!testPhone.trim()) { setErr("Enter a phone number to test."); return; }
    setBusy("test"); setErr(""); setMsg("");
    try {
      await axios.post(`${API}/admin/sms/campaigns/${sel}/test`, { phone: testPhone.trim() }, auth);
      setMsg(`Test text sent to ${testPhone.trim()}.`);
    } catch (e) { setErr(e.response?.data?.detail || "Could not send test."); } finally { setBusy(""); }
  };

  const sendNow = async () => {
    if (sel === "new") { setErr("Save first."); return; }
    if (!window.confirm(`Text this message to ${aud.count ?? "all"} recipient(s)? This cannot be undone.`)) return;
    setBusy("send"); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/sms/campaigns/${sel}/send`, {}, auth);
      setMsg(`Texting ${data.recipients} recipient(s)…`);
      setTimeout(load, 2000); setTimeout(load, 7000);
    } catch (e) { setErr(e.response?.data?.detail || "Could not send."); } finally { setBusy(""); }
  };

  const remove = async () => {
    if (sel === "new") { setSel(null); return; }
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/sms/campaigns/${sel}`, auth);
      setRows((r) => r.filter((x) => x.id !== sel)); setSel(null); setForm(blankSms());
    } catch { setErr("Could not delete."); }
  };

  return (
    <div data-testid="sms-panel">
      {status.ready === false && (
        <div className="mb-5 flex items-start gap-2 rounded-xl bg-gold/10 p-4 text-sm text-ink ring-1 ring-gold/30" data-testid="sms-not-ready">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-gold-deep" />
          <span>Texting isn't switched on yet. Add your <strong>Twilio</strong> keys (Account SID, Auth Token and a sending number) in Secrets, then it activates automatically.</span>
        </div>
      )}
      {status.ready === true && (
        <div className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-sage/20 px-3 py-1.5 text-xs text-ink" data-testid="sms-ready">
          <CheckCircle2 className="h-3.5 w-3.5 text-gold-deep" /> Texting is live — sending from {status.from_number}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg text-ink">Text campaigns</h2>
        <button onClick={openNew} data-testid="sms-new" className="debossed-btn inline-flex items-center gap-2 rounded-full px-4 py-2 font-display text-ink">
          <Plus className="h-4 w-4" /> New text
        </button>
      </div>

      <div className="grid gap-6 sm:grid-cols-[280px_1fr]">
        <div className="space-y-2">
          {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
          {!loading && rows.length === 0 && <p className="rounded-xl bg-ivory-50 p-5 text-center text-sm text-ink-muted ring-1 ring-ivory-400/50">No texts yet.</p>}
          {rows.map((r) => (
            <button key={r.id} onClick={() => openRow(r)} data-testid={`sms-row-${r.id}`}
              className={`block w-full rounded-xl p-3 text-left ring-1 transition ${sel === r.id ? "bg-gold/10 ring-gold/40" : "bg-ivory-50 ring-ivory-400/50 hover:ring-gold/30"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-display text-ink">{r.name || r.body?.slice(0, 24) || "Untitled"}</span>
                <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLE[r.status] || "bg-ink/10 text-ink-soft"}`}>{r.status}</span>
              </div>
              <p className="mt-0.5 truncate text-xs text-ink-muted">{r.body || "No message"}</p>
              {r.status === "sent" && <p className="mt-1 text-[11px] text-ink-muted">{r.stats?.sent || 0} sent · {r.stats?.failed || 0} failed</p>}
            </button>
          ))}
        </div>

        <div>
          {!sel ? (
            <p className="text-sm text-ink-muted">Select a text to edit, or create a new one.</p>
          ) : (
            <div className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/50" data-testid="sms-editor">
              <span className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-wide ${STATUS_STYLE[form.status] || "bg-ink/10 text-ink-soft"}`} data-testid="sms-status">{form.status}</span>
              <div className="mt-4 grid gap-4">
                <label className="text-sm text-ink-soft">Name <span className="text-ink-muted">(internal)</span>
                  <input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="sms-name" disabled={isSent}
                    className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 font-display text-ink disabled:opacity-60" />
                </label>
                <label className="text-sm text-ink-soft">Message
                  <span className="ml-2 text-xs text-ink-muted">{(form.body || "").length} chars · a "Txt STOP to opt out." line is added automatically</span>
                  <textarea value={form.body} onChange={(e) => setField("body", e.target.value)} data-testid="sms-body" rows={5} disabled={isSent}
                    placeholder={"Hi — a short note from Casey…"} className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60" />
                </label>
                <div className="rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50">
                  <p className="flex items-center gap-2 text-sm text-ink"><Users className="h-4 w-4 text-gold-deep" /> Audience <span className="text-xs text-ink-muted">(only contacts with a phone number)</span></p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="text-sm text-ink-soft">Segment
                      <select value={form.segment} onChange={(e) => setField("segment", e.target.value)} data-testid="sms-segment" disabled={isSent}
                        className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60">
                        {segments.map((s) => <option key={s.type} value={s.type}>{s.label} ({s.count})</option>)}
                      </select>
                    </label>
                    <label className="text-sm text-ink-soft">Tag <span className="text-ink-muted">(optional)</span>
                      <select value={form.tag} onChange={(e) => setField("tag", e.target.value)} data-testid="sms-tag" disabled={isSent}
                        className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink disabled:opacity-60">
                        <option value="">Any tag</option>
                        {tags.map((t) => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
                      </select>
                    </label>
                  </div>
                  <p className="mt-3 text-sm text-ink" data-testid="sms-audience-count">
                    {aud.count == null ? "…" : <><span className="font-display text-lg text-gold-deep">{aud.count}</span> phone number{aud.count === 1 ? "" : "s"}</>}
                    {aud.sample?.length > 0 && <span className="ml-2 text-xs text-ink-muted">e.g. {aud.sample.slice(0, 3).join(", ")}…</span>}
                  </p>
                </div>

                {form.status === "sent" && (
                  <div className="grid grid-cols-3 gap-3" data-testid="sms-stats">
                    {[["Recipients", form.stats?.recipients], ["Sent", form.stats?.sent], ["Failed", form.stats?.failed]].map(([k, v]) => (
                      <div key={k} className="rounded-xl bg-ivory-100 p-3 text-center ring-1 ring-ivory-400/50">
                        <p className="font-display text-2xl text-ink">{v ?? 0}</p><p className="text-xs text-ink-muted">{k}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {err && <p className="mt-4 text-sm text-terracotta" data-testid="sms-error">{err}</p>}
              {msg && <p className="mt-4 text-sm text-gold-deep" data-testid="sms-msg">{msg}</p>}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                {!isSent && (
                  <button onClick={save} disabled={busy === "save"} data-testid="sms-save" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink disabled:opacity-50">
                    {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                  </button>
                )}
                {sel !== "new" && !isSent && (
                  <>
                    <div className="inline-flex items-center gap-2">
                      <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="sms-test-phone" placeholder="+15551234567"
                        className="w-40 rounded-full border border-ink/15 bg-white px-3 py-2 text-sm text-ink" />
                      <button onClick={sendTest} disabled={busy === "test"} data-testid="sms-test-send"
                        className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3.5 py-2 text-sm text-ink-soft hover:text-ink disabled:opacity-50">
                        {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Test
                      </button>
                    </div>
                    <button onClick={sendNow} disabled={busy === "send"} data-testid="sms-send-now"
                      className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 hover:bg-ink/90 disabled:opacity-50">
                      {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
                    </button>
                  </>
                )}
                <button onClick={remove} data-testid="sms-delete" className="inline-flex items-center gap-1.5 rounded-full border border-terracotta/40 px-3.5 py-2 text-sm text-terracotta">
                  <Trash2 className="h-4 w-4" /> {sel === "new" ? "Cancel" : "Delete"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Telegram --------------------------- */
function TelegramPanel({ auth }) {
  const [status, setStatus] = useState({ ready: null, token_present: false, bot_username: "", channel_set: false, channel_id: "", subscribers: 0 });
  const [text, setText] = useState("");
  const [target, setTarget] = useState("channel");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [st, h] = await Promise.all([
        axios.get(`${API}/admin/telegram/status`, auth),
        axios.get(`${API}/admin/telegram/broadcasts`, auth),
      ]);
      setStatus(st.data);
      setHistory(h.data.broadcasts || []);
      if (!st.data.channel_set) setTarget("subscribers");
    } catch (e) { if (e.response?.status === 401) adminLogout(); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy("sync"); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/telegram/sync`, {}, auth);
      setMsg(`Synced — ${data.added} new, ${data.subscribers} subscriber(s) total.`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not sync."); } finally { setBusy(""); }
  };

  const broadcast = async () => {
    if (!text.trim()) { setErr("Write a message to broadcast."); return; }
    const where = target === "channel" ? "your channel" : `${status.subscribers} subscriber(s)`;
    if (!window.confirm(`Broadcast this to ${where}?`)) return;
    setBusy("send"); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/telegram/broadcast`, { text: text.trim(), target }, auth);
      setMsg(`Broadcast sent — ${data.sent} delivered${data.failed ? `, ${data.failed} failed` : ""}.`);
      setText(""); load();
    } catch (e) { setErr(e.response?.data?.detail || "Could not broadcast."); } finally { setBusy(""); }
  };

  return (
    <div data-testid="telegram-panel">
      {status.ready === false && (
        <div className="mb-5 flex items-start gap-2 rounded-xl bg-gold/10 p-4 text-sm text-ink ring-1 ring-gold/30" data-testid="tg-not-ready">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-gold-deep" />
          {status.token_present ? (
            <span>The Telegram bot token that was saved looks <strong>invalid</strong> — Telegram didn't recognise it. Re-add a token from <strong>@BotFather</strong> (it looks like <code>123456789:AA...</code>) in Secrets.</span>
          ) : (
            <span>Telegram isn't switched on yet. Create a bot with <strong>@BotFather</strong>, then add the <strong>TELEGRAM_BOT_TOKEN</strong> (and optionally a channel id) in Secrets — it activates automatically.</span>
          )}
        </div>
      )}
      {status.ready === true && (
        <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="tg-ready">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sage/20 px-3 py-1.5 text-xs text-ink">
            <CheckCircle2 className="h-3.5 w-3.5 text-gold-deep" /> Bot live{status.bot_username ? ` — @${status.bot_username}` : ""}
          </span>
          {status.channel_set && <span className="inline-flex items-center gap-1.5 rounded-full bg-ivory-50 px-3 py-1.5 text-xs text-ink-soft ring-1 ring-ivory-400/60"><Hash className="h-3.5 w-3.5" /> Channel {status.channel_id}</span>}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-ivory-50 px-3 py-1.5 text-xs text-ink-soft ring-1 ring-ivory-400/60"><Users className="h-3.5 w-3.5" /> {status.subscribers} subscriber(s)</span>
          <button onClick={sync} disabled={busy === "sync"} data-testid="tg-sync"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-3 py-1.5 text-xs text-ink-soft hover:text-ink disabled:opacity-50">
            {busy === "sync" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync subscribers
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/50" data-testid="tg-composer">
        <h2 className="font-display text-lg text-ink">New broadcast</h2>
        <div className="mt-4 grid gap-4">
          <label className="text-sm text-ink-soft">Send to
            <div className="mt-2 flex gap-2">
              <button type="button" onClick={() => setTarget("channel")} disabled={!status.channel_set} data-testid="tg-target-channel"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition disabled:opacity-40 ${target === "channel" ? "bg-ink text-ivory-50" : "bg-white text-ink-soft ring-1 ring-ink/15 hover:text-ink"}`}>
                <Radio className="h-4 w-4" /> My channel
              </button>
              <button type="button" onClick={() => setTarget("subscribers")} data-testid="tg-target-subscribers"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition ${target === "subscribers" ? "bg-ink text-ivory-50" : "bg-white text-ink-soft ring-1 ring-ink/15 hover:text-ink"}`}>
                <Users className="h-4 w-4" /> Bot subscribers ({status.subscribers})
              </button>
            </div>
            {!status.channel_set && <span className="mt-1 block text-xs text-ink-muted">No channel configured — set TELEGRAM_CHANNEL_ID to post to a channel. Direct-to-subscriber works without one.</span>}
          </label>
          <label className="text-sm text-ink-soft">Message
            <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="tg-text" rows={5}
              placeholder={"A short note to your Telegram readers…"} className="mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink" />
          </label>
          {err && <p className="text-sm text-terracotta" data-testid="tg-error">{err}</p>}
          {msg && <p className="text-sm text-gold-deep" data-testid="tg-msg">{msg}</p>}
          <div>
            <button onClick={broadcast} disabled={busy === "send" || status.ready === false} data-testid="tg-broadcast"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-sm text-ivory-50 hover:bg-ink/90 disabled:opacity-50">
              {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Broadcast
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6" data-testid="tg-history">
        <h3 className="mb-3 font-display text-sm text-ink-soft">Recent broadcasts</h3>
        {history.length === 0 && <p className="text-sm text-ink-muted">No broadcasts yet.</p>}
        <div className="space-y-2">
          {history.map((b) => (
            <div key={b.id} className="rounded-xl bg-ivory-50 p-3 ring-1 ring-ivory-400/50" data-testid={`tg-history-${b.id}`}>
              <p className="truncate text-sm text-ink">{b.text}</p>
              <p className="mt-0.5 text-[11px] text-ink-muted">
                {b.target === "channel" ? "Channel" : "Subscribers"} · {b.stats?.sent || 0} sent{b.stats?.failed ? ` · ${b.stats.failed} failed` : ""} · {new Date(b.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
